import { Stack, Heading, Text, HStack, useDisclosure, Link as ChakraLink } from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { startOfMonth, endOfDay } from 'date-fns';
import { Table } from 'antd';

import SettingsLayout from '@layouts/settings.layout';
import { useSetDocumentTitle } from '@util/hooks';
import { useUserStore } from '@store/app.store';
import FeedRangepicker, { SelectedDateRange } from '@components/feed/feed-rangepicker.component';
import { useBotsUsages } from '@services/query-services/bots.query-service';
import { useUsagesStats } from '@services/query-services/usages.query-service';
import { mergeBotsAndStatsData } from '@util/helpers/settings.helpers';

export default function HoursNylasUsersPage() {
  useSetDocumentTitle('Hours & Nylas Users - FORA');
  const rangePickerDisclosure = useDisclosure();
  const tenantId = useUserStore((state) => state?.tenant?.id);

  const getDefaultStartDate = () => startOfMonth(new Date()).getTime();
  const getDefaultEndDate = () => endOfDay(new Date()).getTime();

  // Default to current month
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);

  const handleReset = () => {
    const start = getDefaultStartDate();
    const end = getDefaultEndDate();
    setStartDate(start);
    setEndDate(end);
    return { start, end };
  };

  const tenantIds = useMemo(() => (tenantId ? [tenantId] : []), [tenantId]);

  const { data: botsUsagesData, isLoading: isLoadingBots } = useBotsUsages({
    tenantIds,
    startDate,
    endDate
  });

  const { data: usagesStatsData, isLoading: isLoadingStats } = useUsagesStats({
    tenantIds,
    startDate,
    endDate
  });

  const mergedData = useMemo(
    () => mergeBotsAndStatsData(botsUsagesData, usagesStatsData),
    [botsUsagesData, usagesStatsData]
  );

  const columns = [
    {
      title: 'Month',
      dataIndex: 'when',
      key: 'when',
      render: (when) =>
        new Date(when).toLocaleString('en-US', {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC'
        })
    },
    {
      title: 'Meeting Hours',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => (duration / 3600).toFixed(2)
    },
    {
      title: 'Meetings',
      dataIndex: 'count',
      key: 'count'
    },
    {
      title: 'Gigs',
      dataIndex: 'bytes_of_media',
      key: 'bytes_of_media',
      render: (bytes) => (bytes / 1073741824).toFixed(2)
    },
    {
      title: 'Tokens',
      dataIndex: 'total_tokens',
      key: 'total_tokens',
      render: (tokens) => tokens.toLocaleString()
    },
    {
      title: 'Nylas Users',
      dataIndex: 'when',
      key: 'billableUsers',
      render: (when) => {
        const date = new Date(when);
        const month = date.getUTCMonth() + 1;
        const year = date.getUTCFullYear();
        return (
          <ChakraLink
            as={Link}
            to={`/settings/nylas-billable-users?month=${month}&year=${year}`}
            color='brandPrimary.primary'
          >
            View Users
          </ChakraLink>
        );
      }
    }
  ];

  const isLoading = isLoadingBots || isLoadingStats;

  return (
    <SettingsLayout pageTitle='Hours & Nylas Users'>
      <Stack
        w='full'
        maxW='1200px'
        gap={6}
      >
        <Text color='surface.onSurfaceVariant'>
          View the number of bot hours used per month and see which users have an associated Nylas
          account for automated bot scheduling.
        </Text>

        {/* Date Range Picker */}
        <HStack>
          <Text fontWeight='medium'>Date Range:</Text>
          <SelectedDateRange
            startDate={startDate}
            endDate={endDate}
            onClick={rangePickerDisclosure.onOpen}
          />
        </HStack>

        <FeedRangepicker
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          disclosure={rangePickerDisclosure}
          onReset={handleReset}
        />

        {/* Bot Hours Section */}
        <Stack
          layerStyle='section-card'
          p={6}
          gap={4}
        >
          <Heading size='sm'>Bot Hours Usage</Heading>
          <Table
            size='small'
            rowKey='when'
            loading={isLoading}
            columns={columns}
            dataSource={mergedData}
            pagination={false}
          />
        </Stack>
      </Stack>
    </SettingsLayout>
  );
}
